/**
 * WHAT SHOWS ON INSIGHTS, WHERE IT SITS, AND HOW BIG IT IS — #269.
 *
 * Vinnie, 26 August 2026: *"I want the choice of what I see. I want this screen
 * to be customizable so someone can choose what they see"* — and, when asked
 * whether this belonged on its own card, that it belongs here. So it covers
 * every tile and panel, not only the two new ones.
 *
 * STYLE B — ONE UNIFORM SNAP GRID (approved). The screen used to place panels
 * with the CSS `order` property inside a few fixed flex sections (`.flow-cols`,
 * `.moving-cols`), which meant a panel could only be nudged among its own
 * section-mates. That is gone. Every panel is now one card on a single 12-column
 * grid: each carries its own {colSpan, rowSpan}, cards snap to whole cells and
 * pack edge-to-edge, and reordering is a drag anywhere on the grid rather than an
 * up/down arrow bounded to a section. A card can also be PINNED — locked in its
 * cell so a drag and the reflow around it leave it where it is.
 *
 * PER BROWSER, like the backlog filters in src/filters.ts and for the same
 * reason: what one person wants to look at is not a decision they get to make
 * for everybody else on the board. Two people can open Insights side by side
 * and see different screens; that is the feature, not a fault.
 *
 * THE ORDER IS THE STORED LIST, and anything not in it is appended in the
 * default order rather than dropped. That matters on the next release: a panel
 * added after somebody saved their arrangement must appear for them, because a
 * new feature nobody can see is the same as one that was never shipped. The same
 * guarantee holds for the new per-panel sizes — a stored layout from before this
 * feature existed (order/hidden only, no spans, no pin) is upgraded to sensible
 * defaults rather than throwing or drawing nothing.
 */
export type PanelId =
  | 'in-flight' | 'throughput' | 'cycle-time' | 'lead-time' | 'keeping-up'
  | 'cumulative-flow' | 'in-your-way' | 'pushed-dates' | 'where-the-work' | 'by-epic'
  | 'forecast';

export const PANEL_LABEL: Record<PanelId, string> = {
  'in-flight': 'Aging work in progress',
  'throughput': 'Throughput',
  'cycle-time': 'Cycle time',
  'lead-time': 'Lead time',
  'keeping-up': 'Net flow',
  'cumulative-flow': 'Cumulative flow',
  'in-your-way': 'Blockers',
  'pushed-dates': 'Repeatedly pushed dates',
  'where-the-work': 'Where the work is',
  'by-epic': 'Epic completion over time',
  'forecast': 'Forecast finish',
};

/*
 * THE FOUR TILES ARE GONE — the merged Insights (#303 follow-up) folded their
 * facts (ready, blocked, open, complete) into the glance line on the scope strip,
 * where they sit beside the controls that decide what they count. There is no
 * longer a tile kind to arrange, so this is empty; the layout keeps working
 * because "is this a tile" is simply always false now.
 */
export const TILES: PanelId[] = [];

/*
 * FOUR ANSWERS, NOT SIX PANELS — #303.
 *
 * `by-epic` is OFF by default, so the rest fit one screen and somebody who wants
 * it turns it on in the "Show panels" checklist.
 *
 * ⚠️ `builds-per-day` MOVED TO RELEASES, unchanged, beside the builds it counts.
 * A stored arrangement naming a panel that no longer exists is repaired rather
 * than honoured — see readLayout.
 */
export const DEFAULT_ORDER: PanelId[] = [
  'in-flight', 'throughput', 'cycle-time', 'lead-time', 'keeping-up',
  'cumulative-flow', 'in-your-way', 'pushed-dates', 'where-the-work', 'by-epic',
  'forecast',
];

/**
 * THE AGING-WIP HERO, THE FIRST THREE FLOW TILES, AND THE FORECAST are on by default.
 * Everything else starts OFF so the screen opens calm rather than as a wall of cards;
 * anyone who wants more turns them on in the "Show panels" checklist (Vinnie, 30 Sep
 * 2026 — "we cannot have all the cards turned on because it just clutters the insight
 * page"). The FORECAST is the exception to "hidden after the first four" (Vinnie, 29 Oct):
 * it is the person-closest-to-the-work's single most-steering read — the p85 finish and
 * the go-live verdict — so it is on by default, and it self-collapses to a compact,
 * honest cold-start card until the board has enough history, so it is never noise.
 */
export const HIDDEN_BY_DEFAULT: PanelId[] = DEFAULT_ORDER.slice(4).filter((id) => id !== 'forecast');

/**
 * THE WHOLE SPANS A CARD MAY TAKE, and nothing between them — a card can never be
 * a lopsided off-grid size, which is what keeps the grid graph-paper neat. The
 * resize handle and the keyboard both step through these lists rather than taking
 * any pixel width. Twelve columns wide, so a card is a third (4), a quarter (3),
 * a half (6), two thirds (8) or the full width (12); one or two row units tall.
 */
export const ALLOWED_COLSPANS = [3, 4, 6, 8, 12] as const;
export const ALLOWED_ROWSPANS = [1, 2] as const;
export const GRID_COLS = 12;

export interface PanelSize {
  /** Whole columns of the 12-column grid, one of ALLOWED_COLSPANS. */
  colSpan: number;
  /** Whole row units, one of ALLOWED_ROWSPANS. */
  rowSpan: number;
  /** Locked in its cell — a drag and the reflow around it skip it. */
  pinned: boolean;
}

/**
 * WHERE EACH PANEL SITS AND HOW BIG IT IS TO BEGIN WITH — chosen so the default
 * order packs the 12-column grid with no dead space: the aging-WIP hero full
 * width (and pinned, so the one number that opens the meeting never gets shuffled
 * away), the four flow tiles a perfectly even 3+3+3+3 band, then two 8+4 rows
 * (the wide cumulative-flow chart beside the blockers list; repeatedly-pushed
 * dates beside where the work is). `by-epic` is full width for when it is turned
 * on.
 */
export const DEFAULT_SIZES: Record<PanelId, PanelSize> = {
  'in-flight': { colSpan: 12, rowSpan: 2, pinned: true },
  'throughput': { colSpan: 3, rowSpan: 1, pinned: false },
  'cycle-time': { colSpan: 3, rowSpan: 1, pinned: false },
  'lead-time': { colSpan: 3, rowSpan: 1, pinned: false },
  'keeping-up': { colSpan: 3, rowSpan: 1, pinned: false },
  'cumulative-flow': { colSpan: 8, rowSpan: 2, pinned: false },
  'in-your-way': { colSpan: 4, rowSpan: 2, pinned: false },
  'pushed-dates': { colSpan: 8, rowSpan: 2, pinned: false },
  'where-the-work': { colSpan: 4, rowSpan: 2, pinned: false },
  'by-epic': { colSpan: 12, rowSpan: 2, pinned: false },
  /* THE FLAGSHIP — the S-curve is only legible full width, so it opens full
     width and PINNED (Vinnie's locked decision this session), the one read the
     screen never shuffles away. Two row units for the plot to breathe. */
  'forecast': { colSpan: 12, rowSpan: 2, pinned: true },
};

export interface Layout {
  order: PanelId[];
  hidden: PanelId[];
  /** Per-panel size and pin. Fully populated after readLayout. */
  sizes: Record<PanelId, PanelSize>;
}

export const DEFAULT_LAYOUT: Layout = {
  order: DEFAULT_ORDER,
  hidden: HIDDEN_BY_DEFAULT,
  sizes: DEFAULT_SIZES,
};

/*
 * THE KEY CARRIES A VERSION so a change to what shows by default can reach people
 * who already have a saved arrangement. Bumped to `-2` on 30 Sep 2026 when the
 * default dropped from "every panel on" to the first four: a saved layout under
 * the old key is left behind (and cleared, below), so everyone lands on the calm
 * four-panel default once, then arranges from there. Bump this again for the next
 * default reset that must reach existing browsers.
 */
export const LAYOUT_KEY = 'flightdeck-insights-2';
const OLD_KEYS = ['flightdeck-insights'];
const known = new Set<PanelId>(DEFAULT_ORDER);
const onlyKnown = (v: unknown): PanelId[] =>
  Array.isArray(v) ? (v.filter((x) => known.has(x as PanelId)) as PanelId[]) : [];

/** Snap a number to the nearest value on a whitelist, defaulting when it is junk. */
const snapTo = (values: readonly number[], v: unknown, fallback: number): number => {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  if (values.includes(v)) return v;
  return values.reduce((best, cur) => (Math.abs(cur - v) < Math.abs(best - v) ? cur : best), values[0]);
};

/**
 * One panel's stored size, repaired to a legal one — a colSpan snapped onto the
 * allowed ladder, a rowSpan onto its own, a boolean pin. Anything missing or
 * nonsensical falls back to the panel's own default, so an old layout with no
 * sizes at all comes back as the defaults rather than as a card of width `NaN`.
 */
const sizeOf = (id: PanelId, raw: unknown): PanelSize => {
  const d = DEFAULT_SIZES[id];
  const s = (raw ?? {}) as Partial<PanelSize>;
  return {
    colSpan: snapTo(ALLOWED_COLSPANS, s.colSpan, d.colSpan),
    rowSpan: snapTo(ALLOWED_ROWSPANS, s.rowSpan, d.rowSpan),
    pinned: typeof s.pinned === 'boolean' ? s.pinned : d.pinned,
  };
};

/**
 * A stored arrangement, repaired rather than trusted.
 *
 * Unknown ids are dropped — a panel removed from the product must not leave a
 * gap somebody cannot clear — and known ids missing from the stored order are
 * appended, so a new panel is never invisible to the people who arranged the
 * screen before it existed. Sizes are read the same way: each panel's stored size
 * is snapped onto the legal ladder, and a panel with no stored size (an old
 * layout, or a newly-shipped panel) takes its default. A fresh screen (no stored
 * value) uses DEFAULT_LAYOUT.
 */
export function readLayout(): Layout {
  try {
    /* A layout saved under a previous key is deliberately not migrated — the point
       of the bump is to reset the default — so clear it once and move on. */
    for (const old of OLD_KEYS) localStorage.removeItem(old);
    const raw = JSON.parse(localStorage.getItem(LAYOUT_KEY) ?? 'null');
    if (!raw || typeof raw !== 'object') return DEFAULT_LAYOUT;
    const order = onlyKnown(raw.order);
    const missing = DEFAULT_ORDER.filter((id) => !order.includes(id));
    const full = [...order, ...missing];
    const storedSizes = (raw.sizes ?? {}) as Record<string, unknown>;
    const sizes = {} as Record<PanelId, PanelSize>;
    for (const id of DEFAULT_ORDER) sizes[id] = sizeOf(id, storedSizes[id]);
    return { order: full, hidden: onlyKnown(raw.hidden), sizes };
  } catch {
    return DEFAULT_LAYOUT;
  }
}

export function writeLayout(l: Layout): void {
  try {
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(l));
  } catch {
    /* A browser refusing to store this is not worth a message: the screen still
       works, it just forgets between visits. */
  }
}

export const showing = (l: Layout, id: PanelId): boolean => !l.hidden.includes(id);

export function toggle(l: Layout, id: PanelId): Layout {
  return showing(l, id)
    ? { ...l, hidden: [...l.hidden, id] }
    : { ...l, hidden: l.hidden.filter((x) => x !== id) };
}

/** Whether a panel is locked in its cell. Guards drag, resize and the reflow. */
export const isPinned = (l: Layout, id: PanelId): boolean => Boolean(l.sizes[id]?.pinned);

/** Lock or unlock a panel in its cell — the pin toggle in its header. */
export function togglePin(l: Layout, id: PanelId): Layout {
  const cur = l.sizes[id] ?? DEFAULT_SIZES[id];
  return { ...l, sizes: { ...l.sizes, [id]: { ...cur, pinned: !cur.pinned } } };
}

/** Set a panel's size, snapping each span onto its legal ladder so a resize can
 *  never leave a lopsided off-grid card. */
export function setSize(l: Layout, id: PanelId, patch: { colSpan?: number; rowSpan?: number }): Layout {
  const cur = l.sizes[id] ?? DEFAULT_SIZES[id];
  const next: PanelSize = {
    ...cur,
    colSpan: patch.colSpan !== undefined ? snapTo(ALLOWED_COLSPANS, patch.colSpan, cur.colSpan) : cur.colSpan,
    rowSpan: patch.rowSpan !== undefined ? snapTo(ALLOWED_ROWSPANS, patch.rowSpan, cur.rowSpan) : cur.rowSpan,
  };
  return { ...l, sizes: { ...l.sizes, [id]: next } };
}

/** Step a span one notch along its ladder — the keyboard resize (arrow keys on
 *  the corner handle). `dir` is +1 to grow, -1 to shrink; it clamps at the ends. */
export function stepSpan(l: Layout, id: PanelId, axis: 'col' | 'row', dir: -1 | 1): Layout {
  if (isPinned(l, id)) return l;
  const cur = l.sizes[id] ?? DEFAULT_SIZES[id];
  const ladder: readonly number[] = axis === 'col' ? ALLOWED_COLSPANS : ALLOWED_ROWSPANS;
  const at = ladder.indexOf(axis === 'col' ? cur.colSpan : cur.rowSpan);
  const nextVal = ladder[Math.max(0, Math.min(ladder.length - 1, at + dir))];
  return setSize(l, id, axis === 'col' ? { colSpan: nextVal } : { rowSpan: nextVal });
}

/**
 * MOVE ONE PANEL TO A NEW PLACE ON THE GRID, WITH PINNED CARDS HELD IN THEIR CELLS.
 *
 * This is the whole of the drag's effect on the model. `beforeId` is the panel the
 * moving card should land just in front of, or `null` to send it to the end — so
 * the drop lands exactly where the ghost slot showed it would. The result keeps
 * every pinned panel at exactly the array index it already held and threads the
 * moving card plus the other movable panels through the slots left over. Because
 * the grid places cards in array order, a pinned card that keeps its index keeps
 * its position on screen while the shuffle flows past it — which is the pinning
 * behaviour Vinnie approved.
 *
 * A pinned card cannot itself be moved (its grip is dead), so moving it is a
 * no-op rather than a silent reshuffle.
 */
export function reorder(l: Layout, id: PanelId, beforeId: PanelId | null): Layout {
  if (isPinned(l, id) || beforeId === id) return l;
  const order = l.order;
  const pinnedHere = (x: PanelId) => isPinned(l, x);

  // The movable panels, in their current relative order, without the one moving.
  const movable = order.filter((x) => !pinnedHere(x) && x !== id);

  // Where in that movable sequence the card should be inserted.
  let at = movable.length;
  if (beforeId) {
    if (!pinnedHere(beforeId)) {
      const bi = movable.indexOf(beforeId);
      if (bi >= 0) at = bi;
    } else {
      // Dropping in front of a pinned card: land after the movable panels that
      // already precede it, so the pinned card keeps its cell and the rest close up.
      at = order.slice(0, order.indexOf(beforeId)).filter((x) => !pinnedHere(x) && x !== id).length;
    }
  }
  const seq = [...movable.slice(0, at), id, ...movable.slice(at)];

  // Rebuild the full order: pinned anchored at their original indices, the movable
  // sequence poured into the gaps around them.
  const result: (PanelId | null)[] = new Array(order.length).fill(null);
  order.forEach((pid, i) => { if (pinnedHere(pid)) result[i] = pid; });
  let s = 0;
  for (let i = 0; i < result.length; i++) if (result[i] === null) result[i] = seq[s++];
  return { ...l, order: result as PanelId[] };
}

/**
 * Move a panel one place earlier or later among the panels that can move — the
 * keyboard reorder on a focused grip. It steps over pinned cards (which hold
 * their cells) rather than stalling against them, and stops at the ends.
 */
export function moveBy(l: Layout, id: PanelId, dir: -1 | 1): Layout {
  if (isPinned(l, id)) return l;
  const order = l.order;
  const idx = order.indexOf(id);
  if (idx < 0) return l;
  let j = idx + dir;
  while (j >= 0 && j < order.length && isPinned(l, order[j])) j += dir;
  if (j < 0 || j >= order.length) return l;
  // Earlier: land in front of that panel. Later: land in front of the one past it
  // (i.e. just after it), or at the end when it is the last card.
  return dir < 0 ? reorder(l, id, order[j]) : reorder(l, id, order[j + 1] ?? null);
}
